<template>
  <b-sidebar
    id="sidebar-right-finance"
    :width="width"
    right
    backdrop
    body-class="p-0"
    no-header
    no-close-on-route-change
    no-close-on-backdrop
    lazy
    @hidden="closeFinanceSidebar"
  >
    <template #default="{ hide }">
      <div class="detail-widget">
        <div class="detail-widget__field detail-widget__left" v-if="!loading">
          <div class="detail-widget__top">
            <b-icon-arrow-bar-left
              class="detail-widget__back cursor-pointer"
              font-scale="2"
              @click="hide"
            />
            <b-skeleton-wrapper>
              <div class="top position-relative">
                <h2 class="title text-light mb-0">{{ detailedItem.invoice_number ? 'Детали счёта' : 'Счет' }}</h2>
                <p class="title-sub-invoice-number">{{ detailedItem.invoice_number ? `#${detailedItem.invoice_number}` : '#Без номера' }}</p>
                <div class="buttons">
                  <button
                    class="mr-2 btn btn-outline-light"
                    v-if="canEdit()"
                    @click="changeEditStatus"
                  >
                    {{ editStatus ? "Отмена" : "Редактировать" }}
                  </button>

                  <button
                    v-if="!isAgent"
                    class="mr-2 btn btn-outline-light"
                    @click="openCreateTaskSidebar"
                  >
                    Создать задачу
                  </button>

                  <button
                    v-if="editStatus"
                    class="mr-2 btn btn-outline-light"
                    @click="onSaveChanges"
                  >
                    Сохранить изменения
                  </button>
                </div>
              </div>
            </b-skeleton-wrapper>

            <button
              class="detail-widget__top-copy-button"
              @click="copyLink"
            >
              <b-icon
                icon="link45deg"
                scale="2"
              ></b-icon>
            </button>
          </div>

          <div class="detail-widget__card">
            <b-overlay class="h-100">
              <tsoft-finance-details
                v-if="editStatus"
                :detailed-item="detailedItem"
                :can-edit="canEdit()"
                :edit-mode="editStatus"
                :additional-data="additionalData"
                :save-changes="saveChanges"
                @updateParentData="updateOpenedData"
              />

              <b-tabs
                lazy
                v-if="!editStatus"
                nav-class="nav-widget"
                nav-wrapper-class="p-0"
              >
                <b-tab @click="deleteQueryTab">
                  <template #title>
                    <div>Детали</div>
                  </template>
                  <tsoft-finance-details
                    :canEdit="canEdit()"
                    :editMode="editStatus"
                    :detailed-item="detailedItem"
                    :additional-data="additionalData"
                  />
                </b-tab>

                <b-tab :active="financeTab === 'finance-payments'">
                    <template #title>
                        <div>Платежи</div>
                    </template>
                    <tsoft-finance-payments
                      :finance_id="detailedItem.id"
                      :additionalGetParameter="`&finance_id=${detailedItem.id}`"
                      :isNeedCreate="isAgent ? false : true"
                      :isNeedSubtotal="true"
                      :isCurrencyOperation="detailedItem.reference_currency_id == 122 ? false : true"
                      :sumRubles="detailedItem.sum || 0"
                      :financePaymentDetails="detailedItem"
                      @updateParentData="loadOneItem"
                      @updateCurrencyPayments="updateCurrencyPayments"
                    />
                  </b-tab>

                  <b-tab :active="financeTab === 'finance-requests'">
                  <template #title>
                    <div>Запросы</div>
                  </template>
                  <tsoft-finance-requests-by-parameters
                    :additionalGetParameter="`&finance_id=${detailedItem.id}`"
                    :isNeedCreate="true"
                    :createRequestObject="{order_id: detailedItem.order_id,
                                            company_id: detailedItem.external_company_id || detailedItem.order.company_id,
                                            finance_id: detailedItem.id,
                                            order_service_id: detailedItem.order_service_id}"
                  />
                </b-tab>

                <b-tab :active="financeTab === 'finance-documents'">
                  <template #title>
                    <div>Документы</div>
                  </template>
                  <tsoft-show-edit-documents
                    :financeId="detailedItem.id"
                    :requestDocuments="detailedItem.id ? true : false"
                    :updateListDocument="singleOrMultiDocuments"
                    @clearDocumentList="clearDocumentList"
                  />
                  <tsoft-create-documents
                    :orderId="detailedItem.order_id"
                    :orderServiceId="detailedItem.order_service_id"
                    :companyId="detailedItem.external_company_id || detailedItem.order.company_id"
                    :financeId="detailedItem.id"
                    :needMultiUploadDocuments="true"
                    @updateViewDocument="documentOutput"
                  />
                </b-tab>
  
                <template v-if="detailedItem.invoice_type === 'outgoing' && (isB2CAccount || isHeadAccount)">
                    <b-tab :active="financeTab === 'finance-splits'">
                        <template #title>
                            <div>Сплит счета</div>
                        </template>
                        <FinanceSplitFinderAndCreator
                            :finance_id="detailedItem.id"
                            :sum_payment="detailedItem.sum_payment"
                            :additionalGetParameter="`&finance_id=${detailedItem.id}`"
                            :isNeedCreate="true"
                            @updateParentData="loadOneItem"
                        />
                    </b-tab>    
                </template>

                <template v-if="!isB2CAccount && !isAgent">
                    <b-tab :active="financeTab === 'finance-virtuals'">
                        <template #title>
                          <span>Виртуальные счета</span>
                        </template>
                        <FinanceVirtualFinderAndCreator
                          :isNeedCreate="false"
                          :order_id="detailedItem.order_id"
                          :additionalGetParameter="`&finance_id=${detailedItem.id}`" 
                        ></FinanceVirtualFinderAndCreator>
                    </b-tab>
                </template>

                <template v-if="!isAgent">
                  <b-tab :active="financeTab === 'finance-task'">
                    <template #title>
                      <span>Задачи</span>
                    </template>
                    <tsoft-history-task
                      :modelName="'finance_id'"
                      :modelId="detailedItem.id"
                      :changeLoadTaskList.sync="changeLoad"
                    />
                  </b-tab>
                </template>
              </b-tabs>
            </b-overlay>
          </div>
        </div>

        <div v-else>
            <b-overlay
                :show="true"
                no-wrap
            >
                <template #overlay>
                    <div class="text-center text-success my-5">
                        <b-spinner variant="success"></b-spinner>
                    </div>
                    <div class="text-center">Ожидайте...</div>
                </template>
            </b-overlay>
        </div>
      </div>

      <CreateTaskWidget
        :financeId="detailedItem.id"
        @setToGetAllHistoryTasks="changeLoad = !changeLoad"
        @openCreateTaskSidebar="openCreateTaskSidebar"
      />
    </template>
  </b-sidebar>
</template>

<script>
import { mapGetters } from 'vuex';
import tsoftFinanceDetails from '@components/finance/FinanceDetails';
import tsoftFinancePayments from '@components/finance/FinancePaymentsFinderAndCreator';
import tsoftFinanceRequestsByParameters from '@components/finance/FinanceRequestsByParameters';
import FinanceSplitFinderAndCreator from '@components/finance/FinanceSplitFinderAndCreator';
import CreateTaskWidget from '@/components/task/CreateTaskWidget.vue';

import { mixinDocuments } from '@/mixins/mixinDocuments';
import { defaultDataItems } from '@/mixins/defaultDataItems';
import { openCreateTaskSidebar } from '@/mixins/openTasksSidebar';
import { copyLink } from '@/mixins';
import { API_FINANCE, API_FINANCE_PAYMENTS } from '@/constants';
import FinanceVirtualFinderAndCreator from '@/components/finance/FinanceVirtualFinderAndCreator';

export default {
  components: {
    tsoftFinanceDetails,
    tsoftFinancePayments,
    tsoftFinanceRequestsByParameters,
    FinanceVirtualFinderAndCreator,
    FinanceSplitFinderAndCreator,
    CreateTaskWidget,
  },

  mixins: [
    mixinDocuments,
    defaultDataItems,
    openCreateTaskSidebar,
    copyLink,
  ],
  props: {
    width: {
      type: String,
      require: true,
    }
  },
  data() {
    return {
      changeLoad: false,
      loading: true,
      additionalData: {
        paidSumCurrency: 0
      },
      detailedItem: {},
      saveChanges: false,
      payments: {}
    }
  },
  computed: {
    ...mapGetters('auth', ['currentUser']),

    editStatus() {
      return this.$route.query['edit-finance'] === 'true';
    },
    financeTab() {
      return this.$route.query['tab-finance'];
    },
    isB2CAccount() {
        return this.currentUser.role.includes('b2c-head-manager') || 
                this.currentUser.role.includes('b2c-manager');
    },
    isHeadAccount() {
        return this.currentUser.role.includes('administrator') || 
                this.currentUser.role.includes('head-accountant') || 
                this.currentUser.role.includes('accountant') || 
                this.currentUser.role.includes('president');
    },
    isAgent() {
      return this.currentUser.role.includes('agent');
    },
  },

  watch: {
    detailedItem: {
      handler() {
        this.$emit('onSaveChanges', this.detailedItem);
      },
      deep: true
    }
  },

  methods: {
    loadOneItem() {
      api.call('get', `${API_FINANCE}/${+this.$route.query.finance}`).then(({data}) => {
        this.detailedItem = data;
        this.loading = false;
        this.loadPayments();
      })
    },
    loadPayments() {
      api.call('get', `${API_FINANCE_PAYMENTS}/?finance_id=${+this.$route.query.finance}`).then(({data}) => {
        this.payments = data.data;
      })
      .then(() => {
        this.calcPaidSummer()
      })
    },
    updateOpenedData() {
      if (this.detailedItemIndex > -1) {
        this.saveChanges = false;
        this.loadOneItem();
      }
    },
    closeFinanceSidebar() {
      const query = Object.assign({}, this.$route.query);
      delete query.finance;
      delete query['edit-finance'];
      delete query['tab-finance'];
      this.$router.replace({ query });
    },
    deleteQueryTab() {
      if (this.$route.query['tab-finance']) {
        const query = Object.assign({}, this.$route.query);
        delete query['tab-finance'];
        this.$router.replace({ query });
      }
    },
    canEdit() {
      if (this.currentUser.role.includes('administrator') ||
        this.currentUser.role.includes('head-accountant') ||
        this.currentUser.role.includes('accountant')) {
        return true
      }
      return false
    },
    changeEditStatus() {
      if (this.editStatus) {
        this.loading = true;
        this.loadOneItem();
      }
      this.saveChanges = false;
      this.$router.push({
        query: {
          ...this.$route.query,
          'edit-finance': !this.editStatus,
        },
      })
    },
    updateCurrencyPayments(sum) {
      this.additionalData.paidSumCurrency = sum
    },
    onSaveChanges() {
      this.$emit('onSaveChanges', this.detailedItem);
      this.saveChanges = true;
    },
    calcPaidSummer() {
      let sum = 0;
      this.payments.forEach((payment, index) => {
        if (payment['sum_currency'] !== null) {
          sum = parseFloat(sum) + parseFloat(payment['sum_currency'])
        } else {
          sum = parseFloat(sum) + parseFloat(payment['sum'])
        }
      })
      this.updateCurrencyPayments(sum);
    },
  },

  mounted() {
      this.loadOneItem();
    },
}
</script>

<style lang="scss" scoped>
$bg-top: #0d6247;

.top {
  display: flex;
  align-items: center;

  @media screen and (max-width: 991px) {
    flex-wrap: wrap;
    padding-top: 5px;
  }
}

.buttons {
  margin-left: auto;
  @media screen and (max-width: 991px) {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 20px;
    margin-left: -40px;
  }
}

.textarea-form {
  min-width: 400px;
  @media screen and (max-width: 991px) {
    min-width: 100%;
  }
}

.title {
  @media screen and (max-width: 991px) {
    font-size: 1rem;
    width: 100%;
  }
}

.subtitle {
  @media screen and (max-width: 991px) {
    font-size: 1.1rem;
    margin-left: -40px;
  }
}

.detail-widget {
  display: flex;
  position: absolute;
  width: 100%;
  height: 100%;
  background: #fff;

  @media screen and (max-width: 991px) {
    flex-direction: column;
    position: relative;
  }

  &__left {
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
    overflow-x: hidden;
    @media screen and (max-width: 991px) {
      min-height: 100vh;
    }
  }

  &__right {
    width: 100%;
    background-color: #f5f5f5;
    position: relative;
    @media screen and (max-width: 991px) {
      max-width: 100% !important;
    }

    &.collapses {
      transition: width 0.3s;
    }
  }

  &__top {
    position: relative;
    color: #fff;
    background-color: $bg-top;
    padding: 15px 1.5rem 15px 3rem;
    min-height: 110px;

    &-copy-button {
      position: absolute;
      top: 60px;
      left: 10px;
      border: none;
      outline: none;
      background-color: transparent;
      color: #fff;
    }
  }

  &__card {
    flex: 1;
    overflow-x: hidden;

    ::v-deep .tabs {
      display: flex;
      height: 100%;
      margin: 0;
      @media screen and (max-width: 991px) {
        flex-direction: column;
      }

      .nav-widget {
        background-color: $bg-top;
        height: 100%;
        flex-wrap: nowrap;
        overflow: auto;
        min-width: 130px;
        width: 100%;
        flex-direction: column;
        @media screen and (max-width: 991px) {
          flex-direction: row;
          height: auto;
          overflow-y: hidden;
        }
        .nav-link {
          text-align: left;
          border-radius: 0 !important;
          padding: 15px 10px !important;
          font-size: 14px !important;
          line-height: 1.1;
          font-weight: 700;
          color: #fff;
          min-width: 100px;
          border: none;
          text-transform: uppercase;
          @media screen and (max-width: 991px) {
            display: flex;
            align-items: center;
            justify-content: center;
            height: 100%;
            padding: 10px !important;
          }
          &.active {
            color: #000 !important;
            background-color: #fff;
          }
          &:hover {
            color: #000 !important;
            background: #fff;
          }
        }
      }

      .tab-content {
        overflow: auto;
        height: 100%;
        width: 100%;
        padding: 15px 22px;
        @media screen and (max-width: 991px) {
          overflow: inherit;
          padding: 15px 5px;
        }

        &::-webkit-scrollbar {
          width: 7px;
          border-radius: 0 !important;
        }
        &::-webkit-scrollbar-thumb {
          border-radius: 0 !important;
          cursor: pointer;
        }
      }
    }
  }

  &__back {
    font-size: 200%;
    position: absolute;
    left: 10px;
    top: 15px;
  }
}
.title-sub-invoice-number {
  position: absolute;
  top: 33px;
  left: 0;
  color: #f8f9fa;
  font-weight: bold;
  font-size: 1.8rem;
  margin: 0;
}
</style>